import { Injectable } from '@angular/core';

/**
 * Sesión del player SCORM cuando corre embebido desde otro origen (`host=web`).
 *
 * En ese modo `app-scorm-player-2` es un iframe *cross-site* dentro de la web de la marca
 * (`producto.cursalab.io` embebiendo `app.cursalab.io`). Ahí el `localStorage` deja de ser confiable:
 * Chrome (≥115) y Firefox lo particionan por sitio y Safari puede denegarlo directamente por ITP.
 * Además, escribir/borrar `token` en `app.cursalab.io` podría pisar la sesión de un usuario que
 * también esté logueado ahí en otra pestaña.
 *
 * El player no necesita persistir nada: reconstruye todo desde la querystring en cada carga. Así que
 * en modo web la sesión vive en memoria y `localStorage` no se toca. Fuera de ese modo —app nativa y
 * flujo web normal— el servicio es transparente y delega en `localStorage`, así que los llamadores
 * pueden usarlo siempre sin cambiar de comportamiento.
 */
@Injectable({
  providedIn: 'root',
})
export class ScormPlayerSessionService {
  private webMode = false;
  private embeddedPlayer = false;
  private memory = new Map<string, string>();

  /**
   * Lo activa `app-scorm-player-2` al iniciar, en modo web y en modo app nativa: esta instancia de
   * la app es solo el player dentro de un iframe. El interceptor lo lee para que un 401 no borre el
   * storage (que puede ser el de la página contenedora si comparten origen) ni navegue a `/login`
   * dentro del player.
   */
  markEmbeddedPlayer() {
    this.embeddedPlayer = true;
  }

  unmarkEmbeddedPlayer() {
    this.embeddedPlayer = false;
  }

  get isEmbeddedPlayer(): boolean {
    return this.embeddedPlayer;
  }

  /** Lo activa `app-scorm-player-2` al detectar `host=web`, antes de escribir nada. */
  enableWebMode() {
    this.webMode = true;
    this.memory.clear();
  }

  get isWebMode(): boolean {
    return this.webMode;
  }

  getItem(key: string): string | null {
    if (this.webMode) {
      return this.memory.has(key) ? this.memory.get(key) : null;
    }
    return localStorage.getItem(key);
  }

  setItem(key: string, value: string) {
    if (this.webMode) {
      this.memory.set(key, value);
      return;
    }
    localStorage.setItem(key, value);
  }

  removeItem(key: string) {
    if (this.webMode) {
      this.memory.delete(key);
      return;
    }
    localStorage.removeItem(key);
  }

  /** `user_data` ya parseado, que es como lo consumen los interceptores y los trackers. */
  getUserData(): any {
    try {
      return JSON.parse(this.getItem('user_data') || '{}');
    } catch (error) {
      return {};
    }
  }

  clear() {
    this.memory.clear();
  }
}
